/**
 * Bridges the module-level react-query client to the React notification stack.
 *
 * The {@link queryClient} is constructed at import time, outside the React
 * tree, so its cache `onError` handlers cannot read the notification context
 * directly. A component inside `NotificationProvider` registers a handler here
 * via {@link registerRateLimitHandler}, and the cache handlers call
 * {@link notifyRateLimited} when a request is rejected with HTTP 429.
 *
 * A single dashboard view fans out many parallel requests, so one rate-limit
 * event typically rejects several queries at once. Notifications are coalesced
 * to one toast per {@link RATE_LIMIT_TOAST_INTERVAL_MS} window so the user sees
 * a single message rather than one per in-flight request.
 */

import { HttpStatusCode } from "axios";

/** Minimum gap between rate-limit toasts; collapses a burst into one. */
export const RATE_LIMIT_TOAST_INTERVAL_MS = 5000;

let handler;
let lastNotifiedAt = 0;

/**
 * Registers the handler invoked when a rate-limit response is seen.
 *
 * @param {() => void} fn - Called (at most once per interval) on a 429.
 * @returns {() => void} Unregister function.
 */
export function registerRateLimitHandler(fn) {
  handler = fn;
  return () => {
    if (handler === fn) {
      handler = undefined;
    }
  };
}

/**
 * Whether a rejected request is a rate-limit (HTTP 429) response.
 *
 * `HttpClient` rejects with an `HttpResponseError`, which carries `.status`
 * directly. `.response.status` is also read, for a rejection raised outside
 * `HttpClient` that is a plain Axios error.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export function isRateLimitError(error) {
  return (
    error?.status === HttpStatusCode.TooManyRequests ||
    error?.response?.status === HttpStatusCode.TooManyRequests
  );
}

/**
 * Invokes the registered handler, debounced to one call per
 * {@link RATE_LIMIT_TOAST_INTERVAL_MS} window. No-op if none is registered.
 */
export function notifyRateLimited() {
  if (!handler) {
    return;
  }

  const now = Date.now();
  if (now - lastNotifiedAt < RATE_LIMIT_TOAST_INTERVAL_MS) {
    return;
  }

  lastNotifiedAt = now;
  handler();
}
